/***************************************************************************
 * Helper functions used on deohs-card block and any future blocks
 * that may be added.
 *
 ***************************************************************************/


import { DARK_BACKGROUND_COLORS, DEFAULT_TEXT_COLOR, WHITE_TEXT_COLOR, DEFAULT_LINK_COLOR, DEFAULT_HEADING_COLOR } from './colors';

/***************************************************************************
 * Determines the correct text color based on the selected background color.
 *
 * If the background color is dark, the text color will be set to white for contrast.
 * Otherwise, it will be set to black
 *
 * @param {string} backgroundColor - The background color to check.
 * @returns {string} The appropriate text color (either white or the default color).
 ***************************************************************************/


export function getTextColor(backgroundColor) {
  return DARK_BACKGROUND_COLORS.includes(backgroundColor) ? WHITE_TEXT_COLOR : DEFAULT_TEXT_COLOR;
}


/***************************************************************************
 * Determines the correct heading and link color based on the selected background color.
 *
 * 1) If background is light grey or white, heading & text are purple
 * 2) If background is dark grey or purple, heading & text are white
 *
 * @param {string} backgroundColor - The background color to check.
 * @returns {string} The appropriate text color (either white or the default color).
 ***************************************************************************/

export function getHeadingAndLinkColor(backgroundColor) {
  return DARK_BACKGROUND_COLORS.includes(backgroundColor) ? WHITE_TEXT_COLOR : DEFAULT_HEADING_COLOR;
}

/***************************************************************************
 * Trims content to a specified length.
 *
 * @param {string} value - The content value to be trimmed.
 * @param {number} maxLength - The maximum length allowed.
 * @param {function} setAttributes - The function to set the attributes in the block.
 ****************************************************************************/

export function handleContentChange(value, maxLength = 1000000, setAttributes) {
    if (value.length > maxLength) {
      value = value.substring(0, maxLength);
    }
    setAttributes({ content: value });
  }


/*****************************************************************************
 * Updates the URL as the user types.
 *
 * @param {string} value
 * @param {function} setAttributes
 ****************************************************************************/

export function handleUrlChange(value, setAttributes) {
  setAttributes({ linkUrl: value.trim() });
}

/*****************************************************************************
 * Adds https to the URL when user leaves the field
 *
 * If we don't use this, and user enters espn.com, the link URL
 * on the front-end becomes uw.edu/espn.com instead of https://espn.com
 *
 * @param {string} linkUrl
 * @param {function} setAttributes
 ****************************************************************************/

export function handleUrlBlur(linkUrl, setAttributes) {
  let value = linkUrl.trim();

  if (value && !value.startsWith('https://') && !value.startsWith('http://')) {
    value = `https://${value}`;
  }

  setAttributes({ linkUrl: value });
}

/*******************************************************************************
 * Clears the image and its attributes.
 *
 * @param {function} setAttributes - The function to set the attributes in block
 ******************************************************************************/

export function clearImage(setAttributes) {
    setAttributes({
      imageUrl: '',
      imageUuid: '',
      imageAlt: '',
    });
  }

/***************************************************************************
 * Clears the image and its attributes, including alt text.
 * This is used when uploading an image or inserting an image by URL
 *
 * @param {function} setAttributes
 * @param {function} setUrlInput
 ***************************************************************************/

export function handleRemoveImage(setAttributes, setUrlInput) {
  clearImage(setAttributes);
  setAttributes({ imageAlt: '' });
  setUrlInput('');
}

/***************************************************************************
 * When user uploads an image to card via side panel, show the image in the card
 * immediately.
 *
 * @param {Object} event
 * @param {string} urlInput
 * @param {function} setAttributes
 ***************************************************************************/

export function handleKeyDown(event, urlInput, setAttributes) {
  if (event.key === 'Enter') {
    event.preventDefault();
    setAttributes({ imageUrl: urlInput });
    event.target.blur();
  }
}

/***************************************************************************
 * Get the ALT text from the media library and display it in side bar.
 * Note. After loading an image from library to your card, if you edit the
 * ALT text of image, it's only saved for that node. It doesn't replace
 * the original ALT text in media library.
 *
 * If an image in the library as no alt text, assign an empty string
 *
 * @param {number} mediaId - The ID of the media item.
 * @param {string} newAltText - The new alt text to be set.
 ****************************************************************************/

export function onSelectMedia(media, setAttributes) {
  if (media && media.url) {
    setAttributes({
      imageAlt: typeof media.alt === 'undefined' ? '' : media.alt,
      imageUuid: media.data && media.data.entity_uuid ? media.data.entity_uuid : '',
      imageUrl: media.url,
    });
  } else {
    // console.error('Invalid media object:', media);
  }
}

/***************************************************************************
 * Validate user input. Check it's not empty before trimming it.
 *
 * @param {string} field - The field to be validated
 * @returns {boolean} - Returns true if the field is valid (non-empty), false otherwise
 ****************************************************************************/

export function validateField(field) {
  return (field || '').trim() !== '';
}

/***************************************************************************
 * Display a validation message. Used together with validateField(field)
 *
 * @param {boolean} isValid - Indicates if the field is valid
 * @param {string} message - The validation message to display
 * @returns {JSX.Element|null} - A paragraph element with the message or null
 ****************************************************************************/

export function ValidationMessage({ isValid, message }) {
  return !isValid ? (
    <p className="text-danger">{message}</p>
  ) : null;
}

/***************************************************************************
 * Adds 'https://' to a URL automatically, but keeps relative URLs intact.
 * Rejects URLs (absolute or relative) that contain spaces.
 *
 * @param {string} url - The URL to check and modify.
 * @returns {string} - The modified URL, or original if invalid.
 ***************************************************************************/

export function handleUrlChange2(url) {
  if (!url || /\s/.test(url)) {
    // Reject empty URLs or those containing spaces
    return url;
  }

  if (url.startsWith('/') || /^https?:\/\//i.test(url)) {
    // Return relative or already valid absolute URLs as-is
    return url;
  }

  // Otherwise, prepend https://
  return `https://${url}`;
}

/***************************************************************************
 * Checks if a URL is valid.
 * - Rejects empty or whitespace-only strings
 * - Rejects URLs (relative or absolute) with any spaces or line breaks
 * - Accepts relative URLs starting with `/`
 * - Accepts absolute URLs with a proper hostname and dot
 *
 * @param {string} url - The URL to check and validate.
 * @returns {boolean} - Whether the URL is valid.
 ***************************************************************************/

export const isValidDomain = (url) => {
  if (!url?.trim()) return false;

  // Reject any whitespace
  if (/\s/.test(url)) return false;

  try {
    // Accept relative URLs starting with `/`
    if (/^\/.+/.test(url)) return true;

    // Parse and validate absolute URLs
    const parsedUrl = new URL(url);
    return !!parsedUrl.hostname && parsedUrl.hostname.includes(".");
  } catch (e) {
    return false;
  }
};


/****************************************************************************
 * Removes potentially unsafe content from input by:
 * - Stripping out <script> tags and their contents
 * - Removing inline event handler attributes like onclick, onmouseover, etc.
 * - Removing all remaining HTML tags (e.g., <h1>, <span>, etc.)
 *
 * Example script to test:
 *   <script>alert('hello')</script>Click here
 *
 * Example HTML to test:
 *   <h2><span style="color: #4b67a1;">This is a demo</span></h2>
 *
 * @param {string} input - The user-provided string to sanitize.
 * @returns {string} - A clean string with scripts and HTML removed.
 ****************************************************************************/

export const sanitizeInput = (input) => {

  const withoutScripts = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  const withoutEventHandlers = withoutScripts.replace(/\son\w+="[^"]*"/gi, '');
  return withoutEventHandlers.replace(/<\/?[^>]+(>|$)/g, '');

};


/****************************************************************************
 * Removes potentially unsafe content from input by:
 * - Stripping out <script> tags and their contents
 * - Removing inline event handler attributes like onclick, onmouseover, etc.
 * - Removing JavaScript URLs from href/src attributes (e.g., href="javascript:...")
 * - Preserves safe HTML tags such as <p>, <strong>, <a>, etc.
 *
 * Example input:
 *   <script>alert('hello')</script>Click here
 *
 * Example HTML to test:
 *   <h2><span style="color: #4b67a1;">This is a demo</span></h2>
 *
 * @param {string} input - The user-provided string to sanitize.
 * @returns {string} - A sanitized string with scripts and unsafe attributes removed,
 *                     while keeping valid HTML.
 ****************************************************************************/


export const sanitizeKeepHTML = (input) => {

  const withoutScripts = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  const withoutEventHandlers = withoutScripts.replace(/\s*on\w+="[^"]*"/gi, '');
  const withoutJsUrls = withoutEventHandlers.replace(/\s*(href|src)\s*=\s*["']javascript:[^"']*["']/gi, '');

  return withoutJsUrls;
};



/******************************************************************************
 * Makes console messages more readable. Used for debugging blocks
 * @param {string} label - A label for the log.
 * @param {object} obj - The object to display.
 *
 * The function formats the log message with colors and styles for better readability.
 *
 * Example usage:
 * debug("Accordion Child - Save", { backgroundColorClass, accordionId, headingId, collapseId });
 *****************************************************************************/

export const debug = (label, obj) => {
  const keys = Object.keys(obj);
  const logParts = [`%c${label}:`];
  const styles = ["color: #ffd54f; font-weight: bold;"]; // label

  keys.forEach((key) => {
    const value = JSON.stringify(obj[key]);
    logParts.push(`\n  %c${key}:%c ${value}`);
    styles.push("color: #ff80ab; font-weight: bold;"); // key
    styles.push("color: #b0bec5;"); // value
  });

  console.log(logParts.join(""), ...styles);
};
